import React from "react";
import { ChevronDown, Filter, X } from "lucide-react";

interface FilterOptions {
  platform: string;
  category: string;
  priceRange: string;
  priceFilterType: string;
  priceMin: string;
  priceMax: string;
}

interface FiltersProps {
  filters: FilterOptions;
  categories: string[];
  priceRanges: { value: string; label: string }[];
  priceFilterTypes: { value: string; label: string }[];
  onFilterChange: (filterType: keyof FilterOptions, value: string) => void;
  onPriceMinChange: (value: string) => void;
  onPriceMaxChange: (value: string) => void;
  onClearFilters: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  filters,
  categories,
  priceRanges,
  priceFilterTypes,
  onFilterChange,
  onPriceMinChange,
  onPriceMaxChange,
  onClearFilters,
}) => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-navy-600" />
          <h2 className="text-lg font-semibold text-navy-900">Filter Templates</h2>
        </div>
        
        <button
  onClick={onClearFilters}
  className="flex items-center gap-2 bg-navy-900 hover:bg-navy-700 text-white px-4 py-2 rounded-lg shadow-md transition-all"
>
  <X className="w-4 h-4" />
  <span className="text-sm font-medium">Reset</span>
</button>

      </div>

      {/* Filters Grid */}
      <div className="flex flex-wrap justify-center gap-4">
        {/* Category Filter */}
        <div className="relative w-full sm:w-56">
          <select
            aria-label="Select Category"
            className="w-full pl-4 pr-10 py-3 rounded-xl border border-navy-100 bg-white text-navy-900 
                     focus:border-navy-300 focus:ring-2 focus:ring-navy-50 appearance-none transition-all"
            value={filters.category}
            onChange={(e) => onFilterChange("category", e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <ChevronDown className="w-5 h-5 text-navy-400 absolute right-3 top-3.5 pointer-events-none" />
        </div>

        {/* Price Type Filter */}
        <div className="relative w-full sm:w-56">
          <select
            aria-label="Select Price Type"
            className="w-full pl-4 pr-10 py-3 rounded-xl border border-navy-100 bg-white text-navy-900 
                     focus:border-navy-300 focus:ring-2 focus:ring-navy-50 appearance-none transition-all"
            value={filters.priceFilterType}
            onChange={(e) => onFilterChange("priceFilterType", e.target.value)}
          >
            <option value="">Price Type</option>
            {priceFilterTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <ChevronDown className="w-5 h-5 text-navy-400 absolute right-3 top-3.5 pointer-events-none" />
        </div>

        {/* Price Range Filter */}
        <div className="relative w-full sm:w-56">
          <select
            aria-label="Select Price Range"
            className="w-full pl-4 pr-10 py-3 rounded-xl border border-navy-100 bg-white text-navy-900 
                     focus:border-navy-300 focus:ring-2 focus:ring-navy-50 appearance-none transition-all"
            value={filters.priceRange}
            onChange={(e) => onFilterChange("priceRange", e.target.value)}
          >
            <option value="">Price Range</option>
            {priceRanges.map((range) => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
          <ChevronDown className="w-5 h-5 text-navy-400 absolute right-3 top-3.5 pointer-events-none" />
        </div>

        {/* Custom Price Inputs */}
        {filters.priceFilterType === 'between' && (
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <div className="relative flex-1 min-w-[200px]">
              <input
                type="number"
                value={filters.priceMin}
                onChange={(e) => onPriceMinChange(e.target.value)}
                placeholder="Minimum price"
                className="w-full pl-4 pr-10 py-3 rounded-xl border border-navy-100 bg-white text-navy-900 
                         focus:border-navy-300 focus:ring-2 focus:ring-navy-50 transition-all"
              />
              <span className="absolute right-3 top-3.5 text-navy-400 text-sm">$</span>
            </div>
            
            <div className="relative flex-1 min-w-[200px]">
              <input
                type="number"
                value={filters.priceMax}
                onChange={(e) => onPriceMaxChange(e.target.value)}
                placeholder="Maximum price"
                className="w-full pl-4 pr-10 py-3 rounded-xl border border-navy-100 bg-white text-navy-900 
                         focus:border-navy-300 focus:ring-2 focus:ring-navy-50 transition-all"
              />
              <span className="absolute right-3 top-3.5 text-navy-400 text-sm">$</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Filters;